import React, { useEffect, useMemo, useState } from 'react';
import '../business/Business.css';

import DesktopOnlyBlock from '../business/components/DesktopOnlyBlock';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import CreateFooter from '../business/components/CreateFooter';
import useHttp from '../../hooks/http.hook';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useBranchNav } from '../../hooks/branchNav.hook';
import { normalizeProductGroupsResponse } from '../../utils/productGroups';
import VariantDetailsForm from './components/VariantDetailsForm';
import ProductDurationField from './components/ProductDurationField';
import { BusinessTypes } from '../../types/BusinessType';

function UpsertProductVariant() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const { productsPath } = useBranchNav();

  const productId = params.productId || null;
  const variantId = params.variantId || null;
  const isEdit = !!variantId;

  const [productName, setProductName] = useState('');
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [barcode, setBarcode] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(null);
  const [businessTypeId, setBusinessTypeId] = useState(BusinessTypes.STORE);
  const [submitError, setSubmitError] = useState(null);
  const [isPrefillLoading, setIsPrefillLoading] = useState(false);

  const { loading, requestWithMeta } = useHttp();

  const parsedPrice = useMemo(() => {
    const normalized = String(price).replace(',', '.').trim();
    if (!normalized) return NaN;
    const n = Number(normalized);
    return Number.isFinite(n) ? n : NaN;
  }, [price]);

  const goBack = () => navigate(productsPath, { replace: true });

  useEffect(() => {
    let cancelled = false;

    async function prefill() {
      if (!productId) {
        setSubmitError('Не найден идентификатор товара');
        return;
      }

      setIsPrefillLoading(true);
      setSubmitError(null);

      try {
        const stateProduct = location?.state?.product ?? null;
        const stateVariant = location?.state?.variant ?? null;
        const stateProductId = stateProduct?._id ?? stateProduct?.id ?? null;

        if (stateProduct && stateProductId && String(stateProductId) === String(productId)) {
          if (cancelled) return;
          setProductName(stateProduct?.name ?? '');

          if (isEdit) {
            const stateVariantId = stateVariant?._id ?? stateVariant?.id ?? null;
            const variants = Array.isArray(stateProduct?.variants) ? stateProduct.variants : [];
            const found =
              stateVariant && stateVariantId && String(stateVariantId) === String(variantId)
                ? stateVariant
                : variants.find((v) => String(v?._id ?? v?.id) === String(variantId));

            if (!found) {
              setSubmitError('Вариант не найден');
              return;
            }

            setTitle(found?.title ?? '');
            setPrice(
              found?.price !== undefined && found?.price !== null ? String(found.price) : ''
            );
            setBarcode(found?.barcode ?? '');
            setDurationMinutes(found?.durationMinutes ?? null);
          }
          return;
        }

        const res = await requestWithMeta('/operator/products', 'GET');
        if (!res?.ok) {
          if (cancelled) return;
          setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить товары');
          return;
        }

        const list = normalizeProductGroupsResponse(res?.data);
        const foundGroup = list.find((p) => String(p?._id ?? p?.id) === String(productId));
        if (!foundGroup) {
          if (cancelled) return;
          setSubmitError('Товар не найден');
          return;
        }

        if (cancelled) return;
        setProductName(foundGroup?.name ?? '');

        if (isEdit) {
          const foundVariant = (foundGroup.variants || []).find(
            (v) => String(v?._id ?? v?.id) === String(variantId)
          );
          if (!foundVariant) {
            setSubmitError('Вариант не найден');
            return;
          }
          setTitle(foundVariant?.title ?? '');
          setPrice(
            foundVariant?.price !== undefined && foundVariant?.price !== null
              ? String(foundVariant.price)
              : ''
          );
          setBarcode(foundVariant?.barcode ?? '');
          setDurationMinutes(foundVariant?.durationMinutes ?? null);
        }
      } finally {
        if (!cancelled) setIsPrefillLoading(false);
      }
    }

    prefill();
    return () => {
      cancelled = true;
    };
  }, [productId, variantId, isEdit, location?.state, requestWithMeta]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await requestWithMeta('/operator/business', 'GET');
      if (cancelled || !res?.ok) return;
      const business = res?.data?.business ?? res?.data ?? null;
      setBusinessTypeId(Number(business?.typeId) === BusinessTypes.RENTAL
        ? BusinessTypes.RENTAL
        : BusinessTypes.STORE);
    })();
    return () => {
      cancelled = true;
    };
  }, [requestWithMeta]);

  const isRental = businessTypeId === BusinessTypes.RENTAL;

  const isSubmitDisabled =
    loading ||
    isPrefillLoading ||
    (!isEdit && String(title).trim().length === 0) ||
    !Number.isFinite(parsedPrice) ||
    parsedPrice < 0 ||
    (isRental && !durationMinutes);

  async function handleSubmit() {
    setSubmitError(null);

    const priceNumber = Number(String(price).replace(',', '.').trim());
    if (!Number.isFinite(priceNumber) || priceNumber < 0) {
      setSubmitError('Цена должна быть числом >= 0');
      return;
    }

    if (isRental && !durationMinutes) {
      setSubmitError('Укажите длительность аренды');
      return;
    }

    if (isEdit) {
      const res = await requestWithMeta('/operator/products/variants', 'PUT', {
        variantId,
        title: String(title ?? '').trim() || null,
        price: priceNumber,
        barcode: String(barcode ?? '').trim() || null,
        ...(isRental ? { durationMinutes } : {}),
      });

      if (!res?.ok) {
        setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось обновить вариант');
        return;
      }

      goBack();
      return;
    }

    if (!String(title).trim()) {
      setSubmitError('Укажите название варианта');
      return;
    }

    const res = await requestWithMeta('/operator/products/variants', 'POST', {
      groupId: productId,
      title: String(title).trim(),
      price: priceNumber,
      barcode: String(barcode ?? '').trim() || null,
      ...(isRental ? { durationMinutes } : {}),
    });

    if (!res?.ok) {
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось добавить вариант');
      return;
    }

    goBack();
  }

  return (
    <div className="shop-create-root">
      <DesktopOnlyBlock />

      <div className="shop-create-mobile">
        <CreateBusinessHeader
          title={isEdit ? 'Редактировать вариант' : 'Добавить вариант'}
          subtitle={
            productName
              ? `«${productName}»`
              : isEdit
                ? 'Измените название, цену и штрихкод'
                : 'Укажите название, цену и штрихкод'
          }
          onBack={goBack}
        />

        <main className="shop-create-content">
          {submitError ? (
            <section className="shop-create-section shop-create-sectionError">
              <div className="shop-create-label">Ошибка</div>
              <div className="shop-create-help shop-create-helpTight">{submitError}</div>
            </section>
          ) : null}

          <VariantDetailsForm
            title={title}
            onTitleChange={setTitle}
            price={price}
            onPriceChange={setPrice}
            barcode={barcode}
            onBarcodeChange={setBarcode}
            requireTitle={!isEdit}
          />
          {isRental ? (
            <ProductDurationField
              durationMinutes={durationMinutes}
              onDurationMinutesChange={setDurationMinutes}
              price={price}
            />
          ) : null}
        </main>

        <CreateFooter
          disabled={isSubmitDisabled}
          label={
            loading
              ? isEdit
                ? 'Сохранение…'
                : 'Добавление…'
              : isEdit
                ? 'Сохранить'
                : 'Добавить вариант'
          }
          onCreate={handleSubmit}
        />
      </div>
    </div>
  );
}

export default UpsertProductVariant;
